import { createContext, useContext, useState } from 'react'
import { User } from '../../utils/types/authTypes'
import { authInit } from '../../utils/initialisations/authInit'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { jwtDecoder } from '../../utils/helpers/helper'
import { useQueryClient } from '@tanstack/react-query'
import { axiosBase } from '../../api/axios'

// Shared with useRefreshToken, which reads it at refresh time so other tabs' refreshes are picked up
export const REFRESH_TOKEN_STORAGE_KEY = 'cl_refresh_token'

type TAuthContext = {
  user: User
  userSignOut: () => void
  userSignIn: (user: User) => void
}

export const AuthContext = createContext<TAuthContext | undefined>(undefined)

export const useAuthContext = () => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthContextProvider')
  }

  return context
}

type AuthContextProviderProps = {
  children: React.ReactNode
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const { removeItem, setItem, getItem } = useLocalStorage('cl_user')
  const refreshTokenStorage = useLocalStorage(REFRESH_TOKEN_STORAGE_KEY)
  const [user, setUser] = useState<User>(() => {
    const storedUser = jwtDecoder(getItem())

    return storedUser
      ? { ...storedUser, refreshToken: refreshTokenStorage.getItem() ?? '' }
      : authInit
  })
  const queryClient = useQueryClient()

  function userSignOut() {
    // Revoke the refresh token on the BE. Errors are ignored, local sign out happens regardless.
    // No Authorization header: an expired or rejected JWT makes the BE 401 before revoking
    const refreshToken = refreshTokenStorage.getItem()
    if (refreshToken) {
      axiosBase
        .post('/api/token/invalidate', { refresh_token: refreshToken })
        .catch(() => {})
    }

    // Clear all React Query cache
    queryClient.clear()

    setUser(authInit)
    removeItem()
    refreshTokenStorage.removeItem()
  }

  function userSignIn(user: User) {
    setUser(user)
    setItem(user.token)

    if (user.refreshToken) {
      refreshTokenStorage.setItem(user.refreshToken)
    } else {
      refreshTokenStorage.removeItem()
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        userSignOut,
        userSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
